import { Component, signal, computed, viewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Property, PropertyImage } from '../../../../features/property/models/property.model';

interface SocialPreset {
  label: string;
  width: number;
  height: number;
  ratio: string;
}

interface OverlayOption {
  id: string;
  label: string;
  url: string;
  opacity: string;
}

@Component({
  selector: 'app-image-generator',
  imports: [
    CommonModule, MatButtonModule, MatSliderModule, 
    MatSlideToggleModule, MatButtonToggleModule, MatIconModule,
    MatDividerModule, MatSelectModule, MatFormFieldModule, MatInputModule
],
  templateUrl: './image-generator.html',
  styleUrl: './image-generator.scss',
})
export class ImageGeneratorComponent {
  data = inject<{
    property: Property;
}>(MAT_DIALOG_DATA);

  propertyImages = signal<string[]>([]);
  // Signals for state
  imageSource = signal<string | null>(null);
  showSafeZones = signal(false);
  logoWidth = signal(150);
  logoTop = signal(10);
  logoLeft = signal(10);

  // Resolution Presets
  presets: SocialPreset[] = [
    { label: 'Square (1:1)', width: 1080, height: 1080, ratio: '1 / 1' },
    { label: 'Portrait (4:5)', width: 1080, height: 1350, ratio: '4 / 5' },
    { label: 'Story (9:16)', width: 1080, height: 1920, ratio: '9 / 16' },
    { label: 'X/FB (1.91:1)', width: 1200, height: 630, ratio: '1.91 / 1' }
  ];
  selectedPreset = signal<SocialPreset>(this.presets[0]);

  // Define your available logos
  overlayOptions: OverlayOption[] = [
    { id: 'color', label: 'Full Color', url: '/assets/images/logo_full_900.png', opacity: '1' },
    { id: 'white', label: 'White', url: '/assets/images/logo_flat.png', opacity: '0.5' },
    { id: 'black', label: 'Single', url: '/assets/images/logo_single.png', opacity: '1' }
  ];
  selectedOverlay = signal<OverlayOption>(this.overlayOptions[0]);

  focalX = signal(50); 
  focalY = signal(50);

  showShadow = signal(false);

  showGlow = signal(false);
  
  // Background Image Crop States
  bgScale = signal(1);      // 1 = 100% size
  bgTranslateX = signal(0); // Pan horizontal
  bgTranslateY = signal(0); // Pan vertical

  // Computed style for the background image
  bgImageStyle = computed(() => ({
    'transform': `translate(${this.bgTranslateX()}%, ${this.bgTranslateY()}%) scale(${this.bgScale()})`,
    'cursor': 'move',
    'object-fit': 'none'
  }));

  constructor() {
    console.log('Received property:', this.data.property);
    
    if (this.data.property?.images && Array.isArray(this.data.property.images)) {
      const urls = this.data.property.images.sort((a: PropertyImage, b: PropertyImage) => a.sortOrder - b.sortOrder).map((img: any) => img.imageUrl);
      this.propertyImages.set(urls);
    }
  }

  resetCrop() {
    this.bgScale.set(1);
    this.bgTranslateX.set(0);
    this.bgTranslateY.set(0);
  }

  bgCropStyle = computed(() => ({
    'object-fit': 'cover',
    'object-position': `${this.focalX()}% ${this.focalY()}%`,
    'width': '100%',
    'height': '100%'
  }));

  // ViewChild for the canvas
  canvasRef = viewChild<ElementRef>('imageCanvas');

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: any) => this.imageSource.set(e.target.result);
    reader.readAsDataURL(file);
  }

  // Inside image-generator.component.ts

  centerHorizontally() {
    this.logoLeft.set(50);
  }

  centerVertically() {
    this.logoTop.set(50);
  }

  async downloadImage() {
    const domtoimage = (await import('dom-to-image-more')).default;
    const node = this.canvasRef()?.nativeElement;
    if (!node) return;

    const preset = this.selectedPreset();
    const scale = preset.width / node.offsetWidth;

    const options = {
      width: preset.width,
      height: preset.height,
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        width: `${node.offsetWidth}px`,
        height: `${node.offsetHeight}px`,
        border: 'none',
        background: 'transparent',
        borderRadius: '0',
        boxShadow: 'none'
      },
      filter: (domNode: any) => !domNode.classList?.contains('safe-zone-container')
    };

    try {
      const dataUrl = await domtoimage.toPng(node, options);
      const link = document.createElement('a');
      link.download = `brand-post.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Download failed', error);
    }
  }
}